import React, { useState, useRef, useCallback } from "react";
import { ASSETS } from "../assetManifest.js";
import { CONSTANTS } from "../data";

interface InventoryItemProps {
  item: {
    id: string;
    name: string;
  };
  quantity: number;
  selected: boolean;
  onSelect: (id: string) => void;
}

// Haptic feedback helper
const triggerHapticFeedback = (type: 'light' | 'medium' | 'heavy' = 'light') => {
  if (CONSTANTS.TOUCH_INTERACTION.HAPTIC_FEEDBACK_ENABLED && 'vibrate' in navigator) {
    const patterns = {
      light: [10],
      medium: [20],
      heavy: [30]
    };
    navigator.vibrate(patterns[type]);
  }
};

const InventoryItem: React.FC<InventoryItemProps> = ({
  item,
  quantity,
  selected,
  onSelect,
}) => {
  const [touchFeedback, setTouchFeedback] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const touchStartTime = useRef<number>(0);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);

  const sprite =
    (ASSETS as any).items?.[item.id]?.path ||
    (ASSETS as any).images?.defaultItem?.path ||
    (ASSETS as any).images?.logo?.path;

  // Determine mobile sizing
  const isMobile = window.innerWidth < CONSTANTS.MOBILE_UI.MOBILE_BREAKPOINT;
  const isSmallMobile = window.innerWidth < CONSTANTS.MOBILE_UI.SMALL_MOBILE_BREAKPOINT;

  const itemSize = isSmallMobile 
    ? CONSTANTS.MOBILE_UI.INVENTORY_ITEM_SIZE_SMALL_MOBILE 
    : isMobile 
    ? CONSTANTS.MOBILE_UI.INVENTORY_ITEM_SIZE_MOBILE 
    : 64; // Default desktop size

  const iconSize = isSmallMobile 
    ? CONSTANTS.MOBILE_UI.INVENTORY_ITEM_ICON_SIZE_SMALL_MOBILE 
    : isMobile 
    ? CONSTANTS.MOBILE_UI.INVENTORY_ITEM_ICON_SIZE_MOBILE 
    : 48; // Default desktop size

  const textSize = isSmallMobile 
    ? CONSTANTS.MOBILE_UI.INVENTORY_ITEM_TEXT_SIZE_SMALL_MOBILE 
    : isMobile 
    ? CONSTANTS.MOBILE_UI.INVENTORY_ITEM_TEXT_SIZE_MOBILE 
    : 12; // Default desktop size

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    setIsPressed(true);
    setTouchFeedback(true);
    touchStartTime.current = Date.now();
    hasMoved.current = false;
    
    const touch = e.touches[0];
    touchStartPos.current = { x: touch.clientX, y: touch.clientY };
    
    triggerHapticFeedback('light');
    
    setTimeout(() => setTouchFeedback(false), CONSTANTS.TOUCH_INTERACTION.TOUCH_FEEDBACK_DURATION);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPressed) return;
    
    const touch = e.touches[0];
    const distance = Math.sqrt(
      Math.pow(touch.clientX - touchStartPos.current.x, 2) + 
      Math.pow(touch.clientY - touchStartPos.current.y, 2)
    );
    
    if (distance > CONSTANTS.TOUCH_INTERACTION.DRAG_THRESHOLD) {
      hasMoved.current = true;
      setIsPressed(false); // Cancel the press if user is scrolling
    }
  }, [isPressed]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    setIsPressed(false);
    
    // Only trigger selection if it was a tap (not a scroll/drag)
    if (!hasMoved.current) {
      const touchDuration = Date.now() - touchStartTime.current;
      
      // Normal tap
      if (touchDuration < CONSTANTS.TOUCH_INTERACTION.LONG_PRESS_DURATION) {
        onSelect(item.id);
        triggerHapticFeedback('medium');
      }
    }
  }, [onSelect, item.id]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    // Prevent double-firing on devices that support both touch and mouse
    if (touchStartTime.current && Date.now() - touchStartTime.current < 500) {
      return;
    }
    
    onSelect(item.id);
    setTouchFeedback(true);
    setTimeout(() => setTouchFeedback(false), CONSTANTS.TOUCH_INTERACTION.TOUCH_FEEDBACK_DURATION);
  }, [onSelect, item.id]);

  // Truncate name for mobile
  const displayName = isMobile && item.name.length > 8 
    ? item.name.slice(0, 6) + '...' 
    : item.name;

  return (
    <button
      data-tutorial-id={`item-${item.id}`}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      title={item.name}
      className={`relative rounded-lg border transition-all overflow-hidden group select-none ${
        selected
          ? "border-accent bg-accent/10 ring-2 ring-accent"
          : "border-border-color bg-surface hover:border-text-secondary"
      } ${
        isPressed ? 'scale-95 bg-primary-action/10' : ''
      } ${
        touchFeedback ? 'ring-2 ring-primary-action/50' : ''
      }`}
      style={{
        width: `${itemSize}px`,
        height: `${itemSize}px`,
        minHeight: `${Math.max(itemSize, CONSTANTS.TOUCH_INTERACTION.MIN_TOUCH_TARGET_SIZE)}px`,
        minWidth: `${Math.max(itemSize, CONSTANTS.TOUCH_INTERACTION.MIN_TOUCH_TARGET_SIZE)}px`,
        padding: `${Math.max(2, Math.floor(itemSize * 0.05))}px`,
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div className="w-full h-full flex flex-col items-center justify-center">
        <img
          src={sprite}
          alt={item.name}
          className={`object-contain pointer-events-none select-none transition-transform ${
            isPressed ? 'scale-90' : 'group-hover:scale-105'
          }`}
          style={{
            width: `${iconSize}px`,
            height: `${iconSize}px`,
          }}
          draggable={false}
        />
        
        {/* Quantity badge - positioned absolutely for better space usage */}
        <div
          className={`absolute top-1 right-1 px-1 py-0.5 rounded-full font-bold transition-colors ${
            selected ? "bg-accent text-white" : "bg-border-color text-text-secondary"
          }`}
          style={{
            fontSize: `${Math.max(8, textSize - 2)}px`,
            lineHeight: 1,
            minWidth: `${Math.max(16, textSize + 4)}px`,
            height: `${Math.max(16, textSize + 4)}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {quantity}
        </div>
        
        {/* Item name - positioned at bottom */}
        <div 
          className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-center px-1 py-0.5 truncate"
          style={{
            fontSize: `${textSize}px`,
            lineHeight: 1.1,
          }}
        >
          {displayName}
        </div>
      </div>
      
      {/* Touch ripple effect */}
      {touchFeedback && (
        <div className="absolute inset-0 bg-primary-action/20 rounded-lg animate-ping pointer-events-none" />
      )}
    </button>
  );
};

export default React.memo(InventoryItem);
